/**
 * Particle system types, interfaces, and constants.
 */

import type { RenderTexture, Sprite } from 'pixi.js';

export interface Particle {
  sprite: Sprite;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  startScale: number;
  startAlpha: number;
  stretch: number; // >1 = elongate along velocity direction
  gravity: number;
  friction: number;
  rotationSpeed: number;
  addBlend: boolean;
  hard: boolean; // use hard texture vs soft glow
  active: boolean;
  freezeImmune: boolean; // Sakurai: hit markers keep moving during hitstop
}

export interface Shockwave {
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  expandSpeed: number;
  color: number;
  lineWidth: number;
  life: number;
  maxLife: number;
  freezeImmune: boolean;
}

export interface BeamLine {
  x: number;         // horizontal position
  startY: number;    // top of beam (0 = sky)
  endY: number;      // bottom of beam (impact point)
  color: number;     // beam color
  width: number;     // base width of core line
  life: number;      // remaining life (seconds)
  maxLife: number;    // total life (for alpha calc)
  freezeImmune: boolean;
}

// Sakurai: crisp straight lines radiating from impact — contrast against soft curves
export interface ImpactLine {
  x: number;
  y: number;
  angle: number;
  innerRadius: number;
  outerRadius: number;
  expandSpeed: number;
  color: number;
  lineWidth: number;
  life: number;
  maxLife: number;
  freezeImmune: boolean;
}

export interface PendingBlast {
  timestamp: number;
  callback: () => void;
}

// Gem color palettes keyed by weapon color — shades darker and lighter, no green
export const GEM_PALETTES: Record<number, number[]> = {
  0x00ffff: [0x00ffff, 0x66ffff, 0x00ccdd, 0x00aacc, 0x88eeff, 0x0099bb, 0x44ddee, 0xaaffff],
  0xff00ff: [0xff00ff, 0xff44ff, 0xcc00cc, 0xaa00aa, 0xff88ff, 0x9900aa, 0xdd44dd, 0xffaaff],
};
export const DEFAULT_GEM_PALETTE = [0x00ffff, 0x66ffff, 0x00ccdd, 0x00aacc, 0x88eeff, 0x0099bb, 0x44ddee, 0xaaffff];

// Gold/amber gem palette for Momentum Burst
export const BURST_GEM_PALETTE = [0xffbb00, 0xffcc00, 0xff9900, 0xffdd44, 0xffaa00, 0xeea000, 0xffd700, 0xffee88];

export const POOL_SIZE = 1500;

export interface ParticleConfig {
  x: number;
  y: number;
  color: number;
  life: number;
  scale: number;
  vx?: number;            // default 0
  vy?: number;            // default 0
  alpha?: number;         // default 1
  stretch?: number;       // default 1
  gravity?: number;       // default 0
  friction?: number;      // default 1 (no decay)
  rotationSpeed?: number; // default: random ±3 when stretch ≤ 1, 0 when stretch > 1
  rotation?: number;      // default: random when stretch ≤ 1, atan2(vy,vx) when stretch > 1
  addBlend?: boolean;     // default true
  hard?: boolean;         // default true (hardGlowTexture)
  freezeImmune?: boolean; // default false
}

/**
 * Configure a particle from the pool. Sets all 15 data fields + syncs sprite.
 * Eliminates the manual-init boilerplate — every field gets a value (no stale pool state).
 */
export function configParticle(p: Particle, core: ParticleCore, c: ParticleConfig): void {
  const stretch = c.stretch ?? 1;
  const hard = c.hard ?? true;
  const addBlend = c.addBlend ?? true;

  p.x = c.x;
  p.y = c.y;
  p.vx = c.vx ?? 0;
  p.vy = c.vy ?? 0;
  p.life = c.life;
  p.maxLife = c.life;
  p.startScale = c.scale;
  p.startAlpha = c.alpha ?? 1;
  p.stretch = stretch;
  p.gravity = c.gravity ?? 0;
  p.friction = c.friction ?? 1;
  p.rotationSpeed = c.rotationSpeed ?? (stretch > 1 ? 0 : (Math.random() - 0.5) * 6);
  p.addBlend = addBlend;
  p.hard = hard;
  p.active = true;
  p.freezeImmune = c.freezeImmune ?? false;

  p.sprite.texture = hard ? core.hardGlowTexture : core.softGlowTexture;
  p.sprite.tint = c.color;
  p.sprite.blendMode = addBlend ? 'add' : 'normal';
  p.sprite.visible = true;
  p.sprite.position.set(p.x, p.y);
  if (stretch > 1) {
    p.sprite.rotation = c.rotation ?? Math.atan2(p.vy, p.vx);
    p.sprite.scale.set(c.scale * stretch, c.scale);
  } else {
    p.sprite.rotation = c.rotation ?? Math.random() * Math.PI * 2;
    p.sprite.scale.set(c.scale);
  }
  p.sprite.alpha = p.startAlpha;
}

/** Interface exposing the internal helpers that extracted spawn functions need. */
export interface ParticleCore {
  spawnParticle(
    x: number, y: number,
    color: number,
    speedMin: number, speedMax: number,
    lifeMin: number, lifeMax: number,
    scaleMin: number, scaleMax: number,
    gravity: number,
    friction: number,
    addBlend?: boolean,
    hard?: boolean,
    alpha?: number,
    stretch?: number,
    spawnOffset?: number,
  ): Particle | null;

  spawnParticleAtAngle(
    x: number, y: number,
    angle: number, speed: number,
    color: number,
    life: number,
    scale: number,
    gravity: number,
    friction: number,
    addBlend?: boolean,
    hard?: boolean,
    alpha?: number,
    stretch?: number,
  ): Particle | null;

  spawnShockwave(
    x: number, y: number,
    color: number,
    maxRadius: number,
    expandSpeed: number,
    lineWidth: number,
    life: number,
  ): void;

  spawnBeamLine(
    x: number, startY: number, endY: number,
    color: number, width: number, life: number,
  ): void;

  getInactive(): Particle | null;

  readonly pool: Particle[];
  readonly shockwaves: Shockwave[];
  readonly impactLines: ImpactLine[];
  readonly beamLines: BeamLine[];
  readonly pendingBlasts: PendingBlast[];
  readonly hardGlowTexture: RenderTexture;
  readonly softGlowTexture: RenderTexture;
  readonly clusterTexture: RenderTexture;
}
